// Tarjeta «Movimientos» del SKU: una sola lista con chips por tipo, detalle, cantidad y saldo en piso.
import { useMemo, useState } from 'react';
import { Button, ErrorState, Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { formatDateMx, formatDateTimeMx, formatTimeMx } from '@/lib/dateHelpers';
import { formatMxn, mxn } from '@/lib/dineroHelpers';
import { StatusPill, type PillTone } from '@/shared/patterns/Pills';
import {
    MOVEMENT_CATEGORY_LABEL,
    MOVEMENT_FILTER_LABEL,
    MOVEMENT_FILTERS,
    TRANSFER_STATUS_LABEL,
} from '../../constants';
import {
    formatDelta,
    MOVEMENT_TYPE_LABEL,
    movementAuthorLabel,
    movementReasonLabel,
    storeInfo,
    type StoreDirectory,
    type UserDirectory,
} from '../../lib/inventoryDetail';
import {
    filterMovements,
    movementCounts,
    type MovementCategory,
    type MovementFilter,
    type StockMovementRow,
} from '../../lib/inventoryStockMovements';
import { DETAIL_TD, DETAIL_TH, DetailCard, DetailEmpty } from './DetailCard';

const CATEGORY_TONE: Record<MovementCategory, PillTone> = {
    SALE: 'success',
    ENTRY: 'neutral',
    ADJUSTMENT: 'danger',
    TRANSFER: 'info',
    RETURN: 'warning',
    HOLD: 'neutral',
};

/** Si el libro trae apartados y liberaciones, y cómo alternarlo. */
export interface HoldsToggle {
    shown: boolean;
    onToggle: () => void;
}

/** Carga, error y paginación de la lista. */
export interface MovementsState {
    isPending: boolean;
    error: unknown;
    errorTitle: string;
    errorMessage: (error: unknown) => string;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    onLoadMore: () => void;
    onRetry: () => void;
}

const TD = cn(DETAIL_TD, 'px-1.5 align-top');

function typeLabel(row: StockMovementRow): string {
    return row.category === 'HOLD'
        ? MOVEMENT_TYPE_LABEL[row.type]
        : MOVEMENT_CATEGORY_LABEL[row.category];
}

function storeCode(stores: StoreDirectory, storeId: string | null): string {
    return storeId ? storeInfo(stores, storeId).code : '—';
}

function identifierText(row: StockMovementRow): string | null {
    if (row.identifiers.length === 0) return null;
    if (row.identifiers.length === 1) return `IMEI ${row.identifiers[0]}`;
    return `${String(row.identifiers.length)} IMEI`;
}

/** Parte del renglón secundario; la marcada `danger` va en rojo. */
type DetailPart = string | { text: string; danger: true };

/** Título y renglón secundario del detalle según el tipo. */
function detailOf(
    row: StockMovementRow,
    stores: StoreDirectory,
    users: UserDirectory | null,
): { title: string; sub: DetailPart[] } {
    const author = movementAuthorLabel(row.createdBy, users);
    const imei = identifierText(row);
    const withImei = (parts: DetailPart[]) => (imei ? [...parts, imei] : parts);
    if (row.category === 'TRANSFER' && row.transfer) {
        const t = row.transfer;
        const sub: DetailPart[] = [];
        if (t.status) sub.push(TRANSFER_STATUS_LABEL[t.status]);
        // Si el renglón lleva otra fecha (la de cierre), la de envío va en el detalle.
        if (t.sentAt && Date.parse(t.sentAt) !== Date.parse(row.occurredAt)) {
            sub.push(`enviado ${formatDateTimeMx(t.sentAt)}`);
        }
        return {
            title: `${storeCode(stores, t.fromStoreId)} → ${storeCode(stores, t.toStoreId)}`,
            sub: withImei(sub),
        };
    }
    if (row.category === 'SALE' && row.sale) {
        const s = row.sale;
        return {
            title: `${storeCode(stores, row.storeId || s.storeId)} · ${s.sellerName}`,
            sub: withImei([s.customerName, s.payment.label, formatMxn(mxn(s.amountCents))]),
        };
    }
    const title = `${storeCode(stores, row.storeId)} · ${author}`;
    if (row.category === 'RETURN') {
        return {
            title,
            sub: withImei([
                row.damaged ? { text: 'dañada: no suma', danger: true } : 'Regresa a stock',
            ]),
        };
    }
    if (row.category === 'HOLD') return { title, sub: withImei(['No mueve piezas en piso']) };
    return { title, sub: withImei(row.reason ? [movementReasonLabel(row.reason)] : []) };
}

/** Partes unidas con « · »; sin partes en rojo va como un solo texto. */
function SubLine({ parts }: { parts: readonly DetailPart[] }) {
    if (parts.every((part) => typeof part === 'string')) return <>{parts.join(' · ')}</>;
    return (
        <>
            {parts.map((part, index) => (
                <span key={index}>
                    {index > 0 ? ' · ' : ''}
                    {typeof part === 'string' ? (
                        part
                    ) : (
                        <span className="font-semibold text-danger">{part.text}</span>
                    )}
                </span>
            ))}
        </>
    );
}

function MovementRowView({
    row,
    stores,
    users,
    showBalance,
}: {
    row: StockMovementRow;
    stores: StoreDirectory;
    users: UserDirectory | null;
    showBalance: boolean;
}) {
    const detail = detailOf(row, stores, users);
    return (
        <tr>
            <td className={cn(TD, 'text-[10.5px] tabular-nums text-muted-foreground')}>
                <span className="block">{formatDateMx(row.occurredAt)}</span>
                <span className="block text-[10px]">{formatTimeMx(row.occurredAt)}</span>
            </td>
            <td className={TD}>
                <StatusPill tone={CATEGORY_TONE[row.category]}>{typeLabel(row)}</StatusPill>
            </td>
            <td className={cn(TD, 'whitespace-normal')}>
                <span className="block font-medium">{detail.title}</span>
                {detail.sub.length > 0 ? (
                    <span className="mt-px block text-[10px] text-muted-foreground">
                        <SubLine parts={detail.sub} />
                    </span>
                ) : null}
            </td>
            <td className={cn(TD, 'text-right font-bold tabular-nums')}>
                {formatDelta(row.delta)}
            </td>
            {showBalance ? (
                <td className={cn(TD, 'text-right tabular-nums')}>
                    {row.balance === null ? '—' : row.balance}
                </td>
            ) : null}
        </tr>
    );
}

function FilterChips({
    filters,
    counts,
    active,
    onChange,
}: {
    filters: readonly MovementFilter[];
    counts: Record<MovementFilter, number>;
    active: MovementFilter;
    onChange: (filter: MovementFilter) => void;
}) {
    return (
        <div
            role="group"
            aria-label="Filtrar por tipo"
            className="flex flex-wrap gap-1.5 border-b border-border/40 px-2.5 py-2"
        >
            {filters.map((filter) => {
                const on = filter === active;
                return (
                    <button
                        key={filter}
                        type="button"
                        aria-pressed={on}
                        onClick={() => {
                            onChange(filter);
                        }}
                        className={cn(
                            'rounded-full border px-2 py-px text-[10px] font-semibold transition-colors',
                            on
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-border bg-card text-foreground hover:bg-accent',
                        )}
                    >
                        {`${MOVEMENT_FILTER_LABEL[filter]} ${String(counts[filter])}`}
                    </button>
                );
            })}
        </div>
    );
}

export function MovementsCard({
    rows,
    stores,
    users,
    state,
    periodNote,
    showBalance,
    filters = MOVEMENT_FILTERS,
    emptyText,
    holds,
}: {
    rows: readonly StockMovementRow[];
    stores: StoreDirectory;
    /** Padrón para el autor; `null` mientras carga. */
    users: UserDirectory | null;
    state: MovementsState;
    periodNote: string;
    showBalance: boolean;
    filters?: readonly MovementFilter[];
    emptyText: string;
    holds?: HoldsToggle;
}) {
    const [filter, setFilter] = useState<MovementFilter>('ALL');
    const counts = useMemo(() => movementCounts(rows), [rows]);
    const shown = useMemo(() => filterMovements(rows, filter), [rows, filter]);

    let body;
    if (state.error) {
        body = (
            <ErrorState
                className="m-3"
                title={state.errorTitle}
                description={state.errorMessage(state.error)}
                onRetry={state.onRetry}
            />
        );
    } else if (state.isPending) {
        body = (
            <div className="p-3">
                <Skeleton lines={4} />
            </div>
        );
    } else if (rows.length === 0) {
        // Una página puede venir vacía con nextToken: lo cargado no dice que no haya más.
        body = (
            <DetailEmpty>
                {state.hasNextPage
                    ? 'Aún no hay movimientos en lo cargado; carga más para seguir buscando.'
                    : emptyText}
            </DetailEmpty>
        );
    } else {
        body = (
            <>
                <FilterChips
                    filters={filters}
                    counts={counts}
                    active={filter}
                    onChange={setFilter}
                />
                {shown.length === 0 ? (
                    <DetailEmpty>Sin movimientos de este tipo en lo cargado.</DetailEmpty>
                ) : (
                    <div className="max-h-[588px] overflow-auto">
                        <table className="w-full border-collapse text-[11px]">
                            <thead>
                                <tr>
                                    <th className={cn(DETAIL_TH, 'px-1.5')}>Fecha</th>
                                    <th className={cn(DETAIL_TH, 'px-1.5')}>Tipo</th>
                                    <th className={cn(DETAIL_TH, 'px-1.5')}>Detalle</th>
                                    <th className={cn(DETAIL_TH, 'px-1.5 text-right')}>Cant.</th>
                                    {showBalance ? (
                                        <th
                                            className={cn(DETAIL_TH, 'px-1.5 text-right')}
                                            title="Piezas en piso después del movimiento"
                                        >
                                            Saldo
                                        </th>
                                    ) : null}
                                </tr>
                            </thead>
                            <tbody>
                                {shown.map((row) => (
                                    <MovementRowView
                                        key={row.key}
                                        row={row}
                                        stores={stores}
                                        users={users}
                                        showBalance={showBalance}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </>
        );
    }

    const ready = !state.isPending && !state.error;
    return (
        <DetailCard
            title="Movimientos"
            aside={ready ? `${String(rows.length)} · ${periodNote}` : periodNote}
            flush
        >
            {body}
            {ready && (state.hasNextPage || holds) ? (
                <div className="flex items-center justify-center gap-3 border-t border-border/40 p-2">
                    {state.hasNextPage ? (
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={state.isFetchingNextPage}
                            onClick={state.onLoadMore}
                        >
                            {state.isFetchingNextPage ? 'Cargando…' : 'Cargar más movimientos'}
                        </Button>
                    ) : null}
                    {holds ? (
                        <Button variant="link" size="sm" onClick={holds.onToggle}>
                            {holds.shown ? 'Ocultar apartados' : 'Ver apartados y liberaciones'}
                        </Button>
                    ) : null}
                </div>
            ) : null}
        </DetailCard>
    );
}
